import React from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/shallow';

import type { ExtranetHotelPack } from 'app/typings/core/extranet/hotel/packs';

import { inventoryStore } from '../../../../InventoryStore';
import { useInventoryState, usePackExperiences } from '../../../../hooks';
import { isDateAfterEndOfSale, isDatePast } from '../../../../utils';
import { ScoreTagComponent } from '../../../Common/Score/_components/ScoreTag/ScoreTag';
import { CollapseTable } from '../../../Table/CollapseTable/CollapseTable';

import { PackBookingsRow } from './rows/PackBookingsRow';
import { PackLowestPriceRow } from './rows/PackLowestPriceRow';
import { PackMaxBookingRow } from './rows/PackMaxBookingRow';
import { PackPricingScoreRow } from './rows/PackPricingScoreRow';
import { PackRoomCatRow } from './rows/PackRoomCatRow';
import { PackStatusRow } from './rows/PackStatusRow';
import { PackStockRow } from './rows/PackStockRow';

type Props = {
  pack: ExtranetHotelPack;
  packsScore?: number;
};

const PackTableItemComponent: React.FC<Props> = ({ pack, packsScore }) => {
  const { timePeriod, endOfSaleDate } = useInventoryState();
  const isEditMode = useStore(inventoryStore, (state) => state.isEditMode);
  const packName = pack.nameForHotel || '';

  const { setPricingDrawerOpen, setPackIdForPricingDrawer } = useStore(
    inventoryStore,
    useShallow((state) => ({
      setPricingDrawerOpen: state.setPricingDrawerOpen,
      setPackIdForPricingDrawer: state.setPackIdForPricingDrawer,
    }))
  );

  const { packExperiencesNames, images } = usePackExperiences(pack);

  const isPast = isDatePast(timePeriod.startDate);
  const isAfterEndOfSale = isDateAfterEndOfSale(
    timePeriod.startDate,
    endOfSaleDate
  );

  const displayPriceScore = !isPast && !isAfterEndOfSale;

  const handleScoreClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setPackIdForPricingDrawer(pack.id);
    setPricingDrawerOpen(true);
  };

  return (
    <CollapseTable
      key={`pack-${pack.id}`}
      images={images}
      title={packName}
      keyPanel={`pack-${pack.id}`}
      hasInfo
      info={
        packExperiencesNames.length > 0
          ? packExperiencesNames.join(' + ')
          : undefined
      }
      extraHeader={
        displayPriceScore ? (
          <ScoreTagComponent
            score={packsScore ?? undefined}
            onClick={handleScoreClick}
          />
        ) : undefined
      }
    >
      <PackStatusRow packId={pack.id} />
      <PackStockRow packId={pack.id} />
      <PackBookingsRow packId={pack.id} />
      {isEditMode && <PackMaxBookingRow packId={pack.id} />}
      <PackLowestPriceRow packId={pack.id} />
      <PackPricingScoreRow packId={pack.id} />
      <PackRoomCatRow packId={pack.id} packName={packName} />
    </CollapseTable>
  );
};

export const PackTableItem = React.memo(PackTableItemComponent);
